import { DragAndDrop } from 'ol/interaction';
import olMap from 'ol/Map';
import GPX from 'ol/format/GPX';
import * as olExtent from 'ol/extent';
import Feature from 'ol/Feature';
import { GpxRouteApi, IUserGpxRouteDetail } from './GpxRouteApi';
import { GpxTagEditor } from './GpxTagEditor';
import { summariseGpxContents, describeDiscardedContent, IGpxContentSummary } from './GpxContentSummary';
import {
    createDisplayNameFromFile,
    calculateExtent,
    extractApiErrorMessage,
    getModalById
} from './MapGpxUtils';

/** Matches the Notes column/validation length on the server and the modal's maxlength. */
const MAX_NOTES_LENGTH = 250;

/**
 * Handles GPX file upload via both the file input button and drag-and-drop.
 *
 * When a file is parsed successfully the user is shown the "add route" modal
 * to enter a name, optional notes and optional tags. On confirm, createRoute is
 * called and the result is passed to `onRouteCreated` so the orchestrator can
 * update the store and trigger a feature-layer refresh.
 */
export class GpxUploadHandler {

    private dragAndDropInteraction: DragAndDrop;
    private gpxFormat: GPX = new GPX();
    private addModal: import('bootstrap').Modal | null = null;
    private addModalElement?: HTMLElement;
    private addInput?: HTMLInputElement;
    private addNotesInput?: HTMLTextAreaElement;
    private tagEditor: GpxTagEditor;
    private pendingAddFile: File | null = null;
    private pendingAddGpxXml: string | null = null;
    private pendingAddFeatures: Feature[] | null = null;

    public constructor(
        private map: olMap,
        private api: GpxRouteApi,
        private defaultColour: string,
        private onRouteCreated: (detail: IUserGpxRouteDetail, features: Feature[], extent: olExtent.Extent) => void,
        private showError: (message: string) => void,
        /** A GPX was dropped on the map — the orchestrator surfaces the routes panel. */
        private onGpxDropped: () => void
    ) {
        this.initAddModal();
        this.addGpxFileInputHandler();
        this.addDragDropInteraction();
    }

    private initAddModal(): void {
        const modal = getModalById('gpxAddModal');
        this.addModalElement = modal.element;
        this.addModal = modal.instance;
        this.addInput = document.getElementById('gpxAddInput') as HTMLInputElement;
        this.addNotesInput = document.getElementById('gpxAddNotes') as HTMLTextAreaElement;
        this.tagEditor = new GpxTagEditor(this.api, {
            inputId: 'gpxAddTagInput',
            chipsId: 'gpxAddTagChips',
            suggestionsId: 'gpxAddTagSuggestions'
        });

        if (this.addNotesInput) {
            this.addNotesInput.addEventListener('input', () => {
                this.updateCharacterCount(this.addNotesInput, 'gpxAddNotesCount', MAX_NOTES_LENGTH);
            });
        }

        const confirmButton = document.getElementById('gpxAddConfirmButton') as HTMLButtonElement;
        confirmButton.addEventListener('click', () => {
            void this.executeAddRoute();
        });

        this.addModalElement.addEventListener('shown.bs.modal', () => {
            this.clearValidation('gpxAddForm');
            this.addInput?.focus();
        });

        this.addModalElement.addEventListener('hidden.bs.modal', () => {
            this.clearValidation('gpxAddForm');
            this.cleanupAddModal();
        });
    }

    private addGpxFileInputHandler(): void {
        const self = this;
        $('#gpxFile').on('change', function (e) {
            const files = (e.target as HTMLInputElement).files;
            if (!files) {
                return;
            }
            for (let i = 0; i < files.length; i++) {
                self.processGpxFile(files[i]);
            }
            (e.target as HTMLInputElement).value = '';
        });
    }

    private addDragDropInteraction(): void {
        this.dragAndDropInteraction = new DragAndDrop({
            formatConstructors: [<any>GPX]
        });
        this.dragAndDropInteraction.on('addfeatures', this.onDragDropAddFeatures);
        this.map.addInteraction(this.dragAndDropInteraction);
    }

    private onDragDropAddFeatures = (event): void => {
        //surface the GPX routes panel so the new route is visible in context
        this.onGpxDropped();
        const file: File = event.file;
        this.readFileAsText(file)
            .then(gpxXml => {
                const features = this.readFeaturesFromGpx(gpxXml);
                if (!features) {
                    return;
                }
                this.createRouteFromUpload(file, gpxXml, features);
            })
            .catch(error => {
                console.error('Failed to process dropped GPX file', error);
                this.showError('Unable to read GPX file. Please try again.');
            });
    }

    private processGpxFile(file: File): void {
        this.readFileAsText(file)
            .then(gpxXml => {
                const features = this.readFeaturesFromGpx(gpxXml);
                if (!features) {
                    return;
                }
                this.createRouteFromUpload(file, gpxXml, features);
            })
            .catch(error => {
                console.error('Failed to read GPX file', error);
                this.showError('Unable to read GPX file. Please try again.');
            });
    }

    private readFileAsText(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file, 'UTF-8');
        });
    }

    private readFeaturesFromGpx(gpxXml: string): Feature[] | null {
        const features = this.gpxFormat.readFeatures(gpxXml, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857'
        });
        if (!features || features.length === 0) {
            this.showError('No GPX features found in file.');
            return null;
        }
        return features;
    }

    private createRouteFromUpload(file: File, gpxXml: string, features: Feature[]): void {
        if (!this.addModal || !this.addModalElement || !this.addInput) {
            return;
        }
        this.pendingAddFile = file;
        this.pendingAddGpxXml = gpxXml;
        this.pendingAddFeatures = features;

        //one file becomes one route, so a multi-track file loses everything after
        //the first track — say so here, while Cancel is still an option
        const summary = summariseGpxContents(gpxXml);
        this.showContentWarning(describeDiscardedContent(summary));

        this.addInput.value = createDisplayNameFromFile(file.name);
        if (this.addNotesInput) {
            //prefill from the file's own description — the user can edit or clear it
            this.addNotesInput.value = this.readDescriptionFromGpx(gpxXml, features, summary);
            this.updateCharacterCount(this.addNotesInput, 'gpxAddNotesCount', MAX_NOTES_LENGTH);
        }
        this.tagEditor.reset([]);
        this.addModal.show();
    }

    /** Shows the "what this import will discard" banner, or hides it when message is ''. */
    private showContentWarning(message: string): void {
        const banner = document.getElementById('gpxAddContentWarning');
        const text = document.getElementById('gpxAddContentWarningText');
        if (!banner || !text) {
            return;
        }
        //textContent on the inner span so the warning icon survives
        text.textContent = message;
        banner.classList.toggle('d-none', !message);
    }

    /**
     * The route description carried by the file, truncated to fit the Notes field.
     * Prefers <trk><desc> — that's where our own export writes Notes, so a Trailwise
     * round-trip restores them — and falls back to <metadata><desc>, which is what
     * most third-party tools populate. OpenLayers reads trk/desc into the feature but
     * has no metadata parser at all, hence the raw-XML fallback.
     */
    private readDescriptionFromGpx(gpxXml: string, features: Feature[], summary: IGpxContentSummary): string {
        //waypoints carry their own <desc> and are parsed BEFORE the track (wpt precedes
        //trk in the document), so restrict this to line geometry or a waypoint's
        //description would be mistaken for the route's notes. Of those, read the one
        //line feature the server will keep — OL maps <trk> to MultiLineString and
        //<rte> to LineString, so this follows the same precedence the server applies.
        const lineFeatures = features.filter(feature => feature.getGeometry()?.getType() !== 'Point');
        const keptType = summary.trackCount > 0 ? 'MultiLineString' : 'LineString';
        const keptFeature = lineFeatures.find(feature => feature.getGeometry()?.getType() === keptType)
            ?? lineFeatures[0];

        const trackDescription = <string>keptFeature?.get('desc');
        const description = (typeof trackDescription === 'string' && trackDescription.trim().length > 0)
            ? trackDescription
            : this.readMetadataDescription(gpxXml);
        if (!description) {
            return '';
        }

        //maxlength only constrains typing, not assignment — truncate or the field
        //renders invalid and the confirm-time length check rejects the save
        return description.trim().slice(0, MAX_NOTES_LENGTH);
    }

    /** Reads <gpx><metadata><desc>; returns '' when absent or the XML won't parse. */
    private readMetadataDescription(gpxXml: string): string {
        try {
            const document = new DOMParser().parseFromString(gpxXml, 'application/xml');
            if (document.getElementsByTagName('parsererror').length > 0) {
                return '';
            }
            //getElementsByTagName ignores namespaces, so this works for GPX 1.0/1.1 alike
            const metadata = document.getElementsByTagName('metadata')[0];
            return metadata?.getElementsByTagName('desc')[0]?.textContent ?? '';
        } catch (error) {
            console.error('Failed to read GPX metadata description', error);
            return '';
        }
    }

    /**
     * The extent of the route the server actually stored, read back from the
     * canonical GPX on the create response. Returns null when that yields nothing
     * usable so the caller can fall back to the uploaded file's own extent.
     */
    private readSavedExtent(gpxXml: string): olExtent.Extent | null {
        if (!gpxXml) {
            return null;
        }
        try {
            const features = this.gpxFormat
                .readFeatures(gpxXml, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
                .filter(feature => !!feature.getGeometry());
            if (features.length === 0) {
                return null;
            }
            const extent = calculateExtent(features);
            return olExtent.isEmpty(extent) ? null : extent;
        } catch (error) {
            console.error('Failed to read saved GPX route geometry', error);
            return null;
        }
    }

    private async executeAddRoute(): Promise<void> {
        if (!this.pendingAddFile || !this.pendingAddGpxXml || !this.pendingAddFeatures) {
            return;
        }

        const displayName = (this.addInput?.value ?? '').trim();
        const notesValue = this.addNotesInput?.value ?? '';

        this.clearValidation('gpxAddForm');

        let isValid = true;
        if (!displayName) {
            this.showFieldError(this.addInput, 'Please enter a route name.');
            isValid = false;
        }
        if (notesValue.length > MAX_NOTES_LENGTH) {
            this.showFieldError(this.addNotesInput, `Notes must be ${MAX_NOTES_LENGTH} characters or less.`);
            isValid = false;
        }
        if (!isValid) {
            if (!displayName && this.addInput) {
                this.addInput.focus();
            }
            return;
        }

        const trimmedNotes = notesValue.trim();
        const notes = trimmedNotes.length > 0 ? trimmedNotes : null;
        //commit any half-typed tag before the modal is torn down
        const tags = this.tagEditor.getTags();
        const file = this.pendingAddFile;
        const gpxXml = this.pendingAddGpxXml;
        const features = this.pendingAddFeatures;
        const extent = calculateExtent(features);

        this.cleanupAddModal();
        this.addModal?.hide();

        try {
            const created = await this.api.createRoute({
                displayName,
                colourHex: this.defaultColour,
                gpxXml,
                originalFileName: file.name,
                notes,
                tags
            });
            //zoom to what was actually saved, not to what the file contained: the
            //response carries the canonical GPX (first track + waypoints), so a
            //discarded second track can't drag the extent out
            this.onRouteCreated(created, features, this.readSavedExtent(created.gpxXml) ?? extent);
        } catch (error) {
            this.showError(extractApiErrorMessage(error, 'Unable to save GPX route. Please try again.'));
        }
    }

    public cleanupAddModal(): void {
        this.pendingAddFile = null;
        this.pendingAddGpxXml = null;
        this.pendingAddFeatures = null;
        this.tagEditor?.reset([]);
        this.showContentWarning('');
        if (this.addInput) {
            this.addInput.value = '';
            this.clearFieldError(this.addInput);
        }
        if (this.addNotesInput) {
            this.addNotesInput.value = '';
            this.clearFieldError(this.addNotesInput);
            const counter = document.getElementById('gpxAddNotesCount');
            if (counter) {
                counter.textContent = '0';
                counter.classList.remove('text-warning', 'text-danger');
            }
        }
    }

    private updateCharacterCount(
        textarea: HTMLTextAreaElement | undefined,
        counterId: string,
        maxLength: number
    ): void {
        if (!textarea) {
            return;
        }
        const counter = document.getElementById(counterId);
        if (!counter) {
            return;
        }
        const count = textarea.value.length;
        counter.textContent = count.toString();
        if (count > maxLength) {
            counter.classList.add('text-danger');
            counter.classList.remove('text-warning');
            textarea.classList.add('is-invalid');
        } else if (count > maxLength * 0.9) {
            counter.classList.add('text-warning');
            counter.classList.remove('text-danger');
            textarea.classList.remove('is-invalid');
        } else {
            counter.classList.remove('text-warning', 'text-danger');
            textarea.classList.remove('is-invalid');
        }
    }

    private showFieldError(
        field: HTMLInputElement | HTMLTextAreaElement | null | undefined,
        message?: string
    ): void {
        if (!field) {
            return;
        }
        field.classList.add('is-invalid');
        field.classList.remove('is-valid');
        if (message) {
            const feedback = field.parentElement?.querySelector('.invalid-feedback');
            if (feedback) {
                feedback.textContent = message;
            }
        }
    }

    private clearFieldError(field: HTMLInputElement | HTMLTextAreaElement | null | undefined): void {
        if (!field) {
            return;
        }
        field.classList.remove('is-invalid', 'is-valid');
    }

    private clearValidation(formId: string): void {
        const form = document.getElementById(formId);
        if (!form) {
            return;
        }
        const inputs = form.querySelectorAll('.form-control');
        inputs.forEach(input => {
            input.classList.remove('is-invalid', 'is-valid');
        });
    }
}
